import DOMPurify from 'dompurify';
import { Session } from "../types/api";

class UtilService {
    static readonly EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    // Helper to safely extract ID from string or object
    extractId(value: string | { _id?: string; id?: string; name?: string } | undefined): string {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'object') {
          return value._id || value.id || value.name || '';
        }
        return String(value);
    };

    formatDuration(start: string, end: string): string {
        const startTime = new Date(start).getTime();
        const endTime = new Date(end).getTime();
        const durationMs = endTime - startTime;
        const durationMins = Math.floor(durationMs / 60000);
        const hours = Math.floor(durationMins / 60);
        const minutes = durationMins % 60;
        const seconds = Math.floor((durationMs % 60000) / 1000);
        if (hours > 0) {
            return `${hours}h ${minutes}m ${seconds}s`;
        }
        return `${minutes}m ${seconds}s`;
    }
    formatDateTime = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    };

    sanitizeHtml = (html: string) => {
        return DOMPurify.sanitize(html, {
            ALLOWED_TAGS: [
                'blockquote',
                'br',
                'code',
                'div',
                'em',
                'hr',
                'li',
                'ol',
                'p',
                'pre',
                's',
                'strike',
                'strong',
                'sub',
                'sup',
                'table',
                'tbody',
                'td',
                'tfoot',
                'th',
                'thead',
                'tr',
                'u',
                'ul',
            ],
            ALLOWED_ATTR: [],
        });
    };

    sessionProgramName = (session: Session, withBaseName: boolean = false): string => {
        if ( typeof session.enrollmentId === 'object' && (session.enrollmentId as any).name ) {
            return (session.enrollmentId as any).name + (withBaseName ? ' (' +
                (
                    typeof session.programId === 'object' && (session.programId as any).name
                    ? (session.programId as any).name
                    : this.extractId(session.programId as any)
                ) + ')' : '');
        }
        if ( typeof session.programId === 'object' && (session.programId as any).name ) {
            return (session.programId as any).name;
        }
        return this.extractId(session.programId as any);
    }

    sessionName = (session: Session): string => {
        return session.name || (
            typeof session.sessionDefinitionId === 'object'
            ? (session.sessionDefinitionId as any).name
            : 'Session ' + session.id.slice(0, 8) + '...'
        );
    }

    sessionStatus = (session: Session): string => {
        if ( session.valid === false ) {
            return 'Invalid';
        }
        const age = new Date().getTime() - new Date(session.start).getTime();
        if (session.end) {
            return 'Completed';
        }
        if (age > 1000 * 60 * 60 * 24) {
            return 'Expired';
        }
        if (age > 1000 * 60 * 60 * 2) {
            return 'Incomplete';
        }
        if (age < 1000 * 60 * 30) {
            // Started in the last 30 minutes, but not yet ended?
            // TODO: 30 minutes is just a guess, but it's 2x the length of current max session length
            return 'Live';
        }
        return 'Active';
    }

    sessionStatusClass = (session: Session): string => {
        const status = this.sessionStatus(session);
        switch (status) {
            case 'Invalid':
                return 'bg-gray-200 text-gray-600';
            case 'Completed':
                return 'bg-green-100 text-green-800';
            case 'Expired':
                return 'bg-red-100 text-red-800';
            case 'Incomplete':
                return 'bg-yellow-100 text-yellow-800';
            case 'Active':
                return 'bg-blue-100 text-blue-800';
            case 'Live':
                return 'bg-violet-900 text-white';
            default:
                return 'bg-gray-100 text-gray-800';
        }
  }

  isValidEmail = (email: string): boolean => {
    return UtilService.EMAIL_REGEX.test( email )
  }
}

export const utilService = new UtilService();